/**
 * The editor side of the placement ghost: which asset is being dragged from
 * the Content Browser, and where over the viewport it would land. Browsers
 * hide a drag's data until the drop, so the Content Browser says what it
 * started dragging (`begin`) and the viewport says where it is (`moveTo`).
 *
 * Only the renderer changes: like a gizmo drag, a preview is transient
 * interaction state, and nothing reaches the Command Bus until the drop.
 */
import type { AssetIndexEntry } from "../../assets/content";
import type { Mat4 } from "../../core/math/mat4";
import type { PlacementPreviewDesc } from "../../render/proxies";
import type { RenderBackend } from "../../render/RenderBackend";

export class PlacementPreview {
  private dragged: AssetIndexEntry | null = null;
  private shown: PlacementPreviewDesc | null = null;

  constructor(private readonly backend: Pick<RenderBackend, "setPlacementPreview">) {}

  /** What the renderer is showing, or null. */
  get current(): PlacementPreviewDesc | null {
    return this.shown;
  }

  /** An asset drag began in the Content Browser. */
  begin(asset: AssetIndexEntry): void {
    this.dragged = asset;
  }

  /** The drag is over the viewport: the asset would land with this world matrix. */
  moveTo(world: Mat4): void {
    if (!this.dragged) return;
    this.show({ asset: this.dragged.path, world, bounds: this.dragged.stats?.bounds ?? null });
  }

  /** The drag left the viewport; it may come back. */
  hide(): void {
    if (this.shown) this.show(null);
  }

  /** The drag ended: dropped, cancelled, or let go somewhere else. */
  end(): void {
    this.dragged = null;
    this.hide();
  }

  private show(preview: PlacementPreviewDesc | null): void {
    this.shown = preview;
    this.backend.setPlacementPreview(preview);
  }
}
